/**
 * THE VOICEAGENT PASS ON THE CUSTOMER'S CONVERSATION SCREENS — plan-021 WAPP-HIST (design §33.13.3, §33.13.4, §33.14;
 * ADR 0073 proposed; OD-7, OD-16 at their recommended defaults, AP-0 2026-10-19).
 *
 * A conversation passed between VoiceAgents of one workspace is divided into **VoiceAgent stints**; API-READ answers them
 * on every read the console shows a conversation on (its §7 merge row is this module's contract):
 *
 * | Read | Field | Here |
 * |---|---|---|
 * | History's rows | `entry_voice_agent_id`, `voice_agent_count` | {@link voiceAgentStintRowOfWire} — "2 agents" on a row |
 * | History's figures | `passed_in_conversation_count` | {@link passedInTextOf} — "1 passed in from another agent", "3 passed in from other agents" |
 * | The detail | `voice_agent_stint[]` (≥ 1), `voice_agent_stint_ordinal` on turns and events | dividers, attribution, outcomes |
 * | Live, live activity | `acting_stint` | "Now with {name}", "Agent now" |
 * | Analytics | `voice_agent_pass` (`AnalyticsVoiceAgentPass`) | the "Passes" block |
 * | The test call | `voice_agent_stint[]`, `voice_agent_pass[]` (`TestCallVoiceAgentPass`) | the test console's dividers |
 *
 * EVERY NEW VIEW FIELD IS OPTIONAL, and absent means "this conversation was never passed": a source from before
 * plan-021 (a suite's fixture) renders exactly as today. The api gateway decodes each one as REQUIRED, because the
 * merged spec requires it — a documented field that arrives missing is a server defect, never a single stint.
 *
 * A CONVERSATION WITH ONE STINT AND NO PASS EVENT RENDERS EXACTLY AS TODAY: {@link voiceAgentPassTranscriptOf} answers
 * `null` for it, and the screens then draw their existing transcript — no divider, no name on a line, no new chrome.
 *
 * THE WARM SUMMARY IS ABSENT FOR A READER WITHOUT `transcript.read` (a Viewer: API-READ's `withoutCallerWords`), and
 * then nothing is drawn where it would be — never a placeholder that says a summary existed.
 *
 * Copy is the catalog `voiceAgentPassHistory` (plan-021 §2.12, design §33.14), verbatim in English and Greek. A
 * `PassUnavailableReason` without a phrase of its own reads `reason.other` (design §33.14).
 */
import {
  VOICE_AGENT_PASS_EVENT_CODE,
  VOICE_AGENT_PASS_MODE,
  VOICE_AGENT_STINT_BEGINNING,
  type VoiceAgentPassEventCode,
  type VoiceAgentPassMode,
  type VoiceAgentStintBeginning,
} from "@wetalk/domain";

import { portalText } from "../i18n/portal.js";
import { MalformedResponseError, readArray, readRecord, readText, readWholeNumber } from "./decode.js";
import { formatClock, readNullableText, timelineOfWire } from "./conversation-view.js";
import type { ConversationEventView, TranscriptLineView } from "./gateway.js";

// ---------------------------------------------------------------------------------------
// The views
// ---------------------------------------------------------------------------------------

/** One stretch of a conversation that one VoiceAgent answered (`VoiceAgentStint`). */
export interface VoiceAgentStintView {
  /** 1 for the entry VoiceAgent; each pass adds one. */
  readonly ordinal: number;
  readonly voiceAgentId: string;
  /** The VoiceAgent's name as it is now. */
  readonly displayName: string;
  readonly agentVersionId: string;
  readonly agentVersionLabel: string | null;
  readonly beganBy: VoiceAgentStintBeginning;
  /** The stint that passed the conversation here; `null` for the entry. */
  readonly fromOrdinal: number | null;
  readonly startedAt: string;
  /** `null` while it is answering. */
  readonly endedAt: string | null;
  /** The outcome this stint recorded, in its agent_template's own words. */
  readonly outcome: string | null;
}

/** The VoiceAgent answering a conversation in flight (`ActingStint`). */
export interface ActingStintView {
  readonly ordinal: number;
  readonly voiceAgentId: string;
  readonly displayName: string;
}

/**
 * One `voice_agent_pass` event, structured. Every detail field is read where it is the right type and `null`
 * otherwise: an event's detail is data (design §33.10.2), and a refusal written before the move was decided carries no
 * mode or target.
 */
export interface VoiceAgentPassEventView {
  readonly conversationEventId: string | null;
  /** RFC 3339, where the source carries it (a stored event; a live frame may not). */
  readonly occurredAt: string | null;
  /** The stint the event was written in. */
  readonly voiceAgentStintOrdinal: number;
  readonly code: VoiceAgentPassEventCode;
  readonly mode: VoiceAgentPassMode | null;
  /** `pass_refused` only: a `PassUnavailableReason`. */
  readonly reason: string | null;
  readonly fromOrdinal: number | null;
  readonly toOrdinal: number | null;
  readonly sourceVoiceAgentId: string | null;
  readonly targetVoiceAgentId: string | null;
  /** A warm pass's summary — only for a reader who reads transcripts. */
  readonly summary: string | null;
}

/** Analytics' "Passes" block (`AnalyticsVoiceAgentPass`): counts, never money. */
export interface AnalyticsVoiceAgentPassView {
  readonly passedOut: {
    readonly total: number;
    readonly byTarget: readonly {
      readonly voiceAgentId: string;
      readonly displayName: string;
      readonly mode: VoiceAgentPassMode;
      readonly count: number;
    }[];
  };
  readonly passedIn: {
    readonly total: number;
    readonly bySource: readonly { readonly voiceAgentId: string; readonly displayName: string; readonly count: number }[];
  };
  readonly passedBack: { readonly made: number; readonly received: number };
  readonly refused: {
    readonly total: number;
    readonly byReason: readonly { readonly reason: string; readonly count: number }[];
  };
  /** 0…1, or `null` when this VoiceAgent answered no conversation first in the window. */
  readonly passedShare: number | null;
  /** How long this VoiceAgent answered, in seconds — never billed seconds. */
  readonly timeServedSecond: number;
}

declare module "./gateway.js" {
  interface PastConversationRowView {
    /** plan-021: the VoiceAgent that answered first (`entry_voice_agent_id`). Absent: never passed. */
    readonly entryVoiceAgentId?: string;
    /** plan-021: how many VoiceAgents acted in it, ≥ 1 (`voice_agent_count`). Absent: one. */
    readonly voiceAgentCount?: number;
  }
  interface PastConversationView {
    /** plan-021: the VoiceAgents that answered it, in order (`voice_agent_stint`, ≥ 1 on the wire). */
    readonly voiceAgentStint?: readonly VoiceAgentStintView[];
    /** plan-021: its `voice_agent_pass` events, oldest first. */
    readonly voiceAgentPass?: readonly VoiceAgentPassEventView[];
  }
  interface TranscriptLineView {
    /** plan-021: the stint the line belongs to (`voice_agent_stint_ordinal`); absent reads as 1. */
    readonly voiceAgentStintOrdinal?: number;
  }
  interface ConversationEventView {
    /** plan-021: the pass this timeline entry words, so a screen can name a target the stints do not hold. */
    readonly voiceAgentPass?: VoiceAgentPassEventView;
  }
  interface LiveConversationView {
    /** plan-021: the VoiceAgent answering it now (`acting_stint`). Absent: the board's own VoiceAgent. */
    readonly actingStint?: ActingStintView;
  }
  interface PastConversationPageView {
    /** plan-021: the listed conversations passed in from another VoiceAgent, counted in no other figure. */
    readonly passedInConversationCount?: number;
  }
  interface TestCallView {
    /** plan-021: the VoiceAgents that answered the test; empty before the conversation exists. */
    readonly voiceAgentStint?: readonly VoiceAgentStintView[];
    /** plan-021: the test's `voice_agent_pass` events, oldest first. */
    readonly voiceAgentPass?: readonly VoiceAgentPassEventView[];
  }
  interface VoiceAgentAnalyticsView {
    /** plan-021: the "Passes" block (`voice_agent_pass`). */
    readonly voiceAgentPass?: AnalyticsVoiceAgentPassView;
  }
}

// ---------------------------------------------------------------------------------------
// Decoding (snake_case wire → views). A documented field that is missing or mistyped raises.
// ---------------------------------------------------------------------------------------

function readNullableWholeNumber(value: unknown, path: string): number | null {
  return value === null ? null : readWholeNumber(value, path);
}

function readStintBeginning(value: unknown, path: string): VoiceAgentStintBeginning {
  const raw = readText(value, path);
  const found = VOICE_AGENT_STINT_BEGINNING.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a stint beginning", raw);
  return found;
}

function readVoiceAgentPassMode(value: unknown, path: string): VoiceAgentPassMode {
  const raw = readText(value, path);
  const found = VOICE_AGENT_PASS_MODE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "warm or cold", raw);
  return found;
}

export function voiceAgentStintOfWire(value: unknown, path: string): VoiceAgentStintView {
  const one = readRecord(value, path);
  return {
    ordinal: readWholeNumber(one["ordinal"], `${path}.ordinal`),
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    displayName: readText(one["display_name"], `${path}.display_name`),
    agentVersionId: readText(one["agent_version_id"], `${path}.agent_version_id`),
    agentVersionLabel: readNullableText(one["agent_version_label"], `${path}.agent_version_label`),
    beganBy: readStintBeginning(one["began_by"], `${path}.began_by`),
    fromOrdinal: readNullableWholeNumber(one["from_ordinal"], `${path}.from_ordinal`),
    startedAt: readText(one["started_at"], `${path}.started_at`),
    endedAt: readNullableText(one["ended_at"], `${path}.ended_at`),
    outcome: readNullableText(one["outcome"], `${path}.outcome`),
  };
}

export function voiceAgentStintsOfWire(value: unknown, path: string): readonly VoiceAgentStintView[] {
  return readArray(value, path)
    .map((one, index) => voiceAgentStintOfWire(one, `${path}[${String(index)}]`))
    .sort((left, right) => left.ordinal - right.ordinal);
}

export function actingStintOfWire(value: unknown, path: string): ActingStintView {
  const one = readRecord(value, path);
  return {
    ordinal: readWholeNumber(one["ordinal"], `${path}.ordinal`),
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    displayName: readText(one["display_name"], `${path}.display_name`),
  };
}

/** A History row's two plan-021 fields, both required on the wire. */
export function voiceAgentStintRowOfWire(
  one: Readonly<Record<string, unknown>>,
  path: string,
): { readonly entryVoiceAgentId: string; readonly voiceAgentCount: number } {
  return {
    entryVoiceAgentId: readText(one["entry_voice_agent_id"], `${path}.entry_voice_agent_id`),
    voiceAgentCount: readWholeNumber(one["voice_agent_count"], `${path}.voice_agent_count`),
  };
}

function detailText(detail: Readonly<Record<string, unknown>>, key: string): string | null {
  const value = detail[key];
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function detailWhole(detail: Readonly<Record<string, unknown>>, key: string): number | null {
  const value = detail[key];
  return typeof value === "number" && Number.isInteger(value) && value >= 1 ? value : null;
}

/**
 * A pass event's structured view from its `detail`, or `null` when the detail names no code this console knows (an
 * event is data: a code added later is left out rather than worded wrongly).
 */
export function voiceAgentPassEventOf(input: {
  readonly conversationEventId: string | null;
  readonly occurredAt: string | null;
  readonly voiceAgentStintOrdinal: number;
  readonly detail: unknown;
}): VoiceAgentPassEventView | null {
  const detail: Readonly<Record<string, unknown>> =
    typeof input.detail === "object" && input.detail !== null && !Array.isArray(input.detail)
      ? (input.detail as Record<string, unknown>)
      : {};
  const rawCode = detailText(detail, "code");
  const code = VOICE_AGENT_PASS_EVENT_CODE.find((one) => one === rawCode);
  if (code === undefined) return null;
  const rawMode = detailText(detail, "mode");
  return {
    conversationEventId: input.conversationEventId,
    occurredAt: input.occurredAt,
    voiceAgentStintOrdinal: input.voiceAgentStintOrdinal,
    code,
    mode: VOICE_AGENT_PASS_MODE.find((one) => one === rawMode) ?? null,
    reason: detailText(detail, "reason"),
    fromOrdinal: detailWhole(detail, "from_ordinal"),
    toOrdinal: detailWhole(detail, "to_ordinal"),
    sourceVoiceAgentId: detailText(detail, "source_voice_agent_id"),
    targetVoiceAgentId: detailText(detail, "target_voice_agent_id"),
    summary: detailText(detail, "summary"),
  };
}

/** `TestCallVoiceAgentPass` / a timeline entry of kind `voice_agent_pass`, as stored. */
export function voiceAgentPassEventOfWire(value: unknown, path: string): VoiceAgentPassEventView | null {
  const one = readRecord(value, path);
  return voiceAgentPassEventOf({
    conversationEventId: readText(one["conversation_event_id"], `${path}.conversation_event_id`),
    occurredAt: readText(one["occurred_at"], `${path}.occurred_at`),
    voiceAgentStintOrdinal: readWholeNumber(one["voice_agent_stint_ordinal"], `${path}.voice_agent_stint_ordinal`),
    detail: one["detail"],
  });
}

/**
 * The live transcript topic's `conversation.event` frame for a pass, or `null` for any other frame (RT-WIRE publishes
 * it; design §33.13.3 — "the console draws the same dividers live"). Tolerant on purpose: a frame is a live hint, the
 * stored read is the record, and an unreadable frame is no divider rather than a broken panel.
 */
export function voiceAgentPassFrameOf(data: unknown): VoiceAgentPassEventView | null {
  if (typeof data !== "object" || data === null || Array.isArray(data)) return null;
  const one = data as Record<string, unknown>;
  if (one["kind"] !== "voice_agent_pass") return null;
  const ordinal = one["voice_agent_stint_ordinal"];
  const eventId = one["conversation_event_id"];
  const occurredAt = one["occurred_at"];
  return voiceAgentPassEventOf({
    conversationEventId: typeof eventId === "string" ? eventId : null,
    occurredAt: typeof occurredAt === "string" ? occurredAt : null,
    voiceAgentStintOrdinal: typeof ordinal === "number" && Number.isInteger(ordinal) && ordinal >= 1 ? ordinal : 1,
    detail: one["detail"],
  });
}

/**
 * Each stored line's stint ordinal, off the wire's turns (`voice_agent_stint_ordinal`, required on History's turns and
 * on the test call's stored lines). The lines are `transcriptOfWire`'s, in the same order.
 */
export function withVoiceAgentStintOrdinal(
  line: readonly TranscriptLineView[],
  wire: unknown,
  path: string,
): readonly TranscriptLineView[] {
  const turn = readArray(wire, path);
  return line.map((one, index) => {
    const at = `${path}[${String(index)}]`;
    const ordinal = readWholeNumber(readRecord(turn[index], at)["voice_agent_stint_ordinal"], `${at}.voice_agent_stint_ordinal`);
    return { ...one, voiceAgentStintOrdinal: ordinal };
  });
}

/**
 * History's timeline with its pass events worded (design §33.13.3) and the same events structured. A `pass_started` is
 * the start of what the completed or refused event that follows it says, so it is not a timeline entry of its own.
 */
export function timelineWithVoiceAgentPassOf(
  value: unknown,
  path: string,
  now: Date,
  stint: readonly VoiceAgentStintView[],
): { readonly event: readonly ConversationEventView[]; readonly voiceAgentPass: readonly VoiceAgentPassEventView[] } {
  const event: ConversationEventView[] = [];
  const voiceAgentPass: VoiceAgentPassEventView[] = [];
  readArray(value, path).forEach((entry, index) => {
    const at = `${path}[${String(index)}]`;
    const one = readRecord(entry, at);
    if (readText(one["kind"], `${at}.kind`) !== "voice_agent_pass") {
      event.push(...timelineOfWire([entry], at, now));
      return;
    }
    const found = voiceAgentPassEventOfWire(entry, at);
    if (found === null) return;
    voiceAgentPass.push(found);
    if (found.code === "pass_started") return;
    const occurredAt = readText(one["occurred_at"], `${at}.occurred_at`);
    const timeline: ConversationEventView = {
      get atText() {
        return formatClock(occurredAt);
      },
      get what() {
        return voiceAgentPassSentenceOf(found, voiceAgentNameOf(stint, []), null) ?? "";
      },
      voiceAgentPass: found,
    };
    event.push(timeline);
  });
  return { event, voiceAgentPass };
}

/** The test call's plan-021 fields (`TestCall.voice_agent_stint`, `TestCall.voice_agent_pass`), both required. */
export function testCallVoiceAgentPassOfWire(
  data: Readonly<Record<string, unknown>>,
  operation: string,
): { readonly voiceAgentStint: readonly VoiceAgentStintView[]; readonly voiceAgentPass: readonly VoiceAgentPassEventView[] } {
  return {
    voiceAgentStint: voiceAgentStintsOfWire(data["voice_agent_stint"], `${operation}.voice_agent_stint`),
    voiceAgentPass: readArray(data["voice_agent_pass"], `${operation}.voice_agent_pass`).flatMap((one, index) => {
      const found = voiceAgentPassEventOfWire(one, `${operation}.voice_agent_pass[${String(index)}]`);
      return found === null ? [] : [found];
    }),
  };
}

function countRowsOfWire<Row>(value: unknown, path: string, rowOf: (one: Readonly<Record<string, unknown>>, at: string) => Row): readonly Row[] {
  return readArray(value, path).map((one, index) => {
    const at = `${path}[${String(index)}]`;
    return rowOf(readRecord(one, at), at);
  });
}

/** `VoiceAgentAnalytics.voice_agent_pass` (design §33.13.4). */
export function analyticsVoiceAgentPassOfWire(value: unknown, path: string): AnalyticsVoiceAgentPassView {
  const one = readRecord(value, path);
  const passedOut = readRecord(one["passed_out"], `${path}.passed_out`);
  const passedIn = readRecord(one["passed_in"], `${path}.passed_in`);
  const passedBack = readRecord(one["passed_back"], `${path}.passed_back`);
  const refused = readRecord(one["refused"], `${path}.refused`);
  const share = one["passed_share"];
  if (share !== null && (typeof share !== "number" || !Number.isFinite(share) || share < 0 || share > 1)) {
    throw new MalformedResponseError(`${path}.passed_share`, "a share between 0 and 1, or null", share);
  }
  return {
    passedOut: {
      total: readWholeNumber(passedOut["total"], `${path}.passed_out.total`),
      byTarget: countRowsOfWire(passedOut["by_target"], `${path}.passed_out.by_target`, (row, at) => ({
        voiceAgentId: readText(row["voice_agent_id"], `${at}.voice_agent_id`),
        displayName: readText(row["display_name"], `${at}.display_name`),
        mode: readVoiceAgentPassMode(row["mode"], `${at}.mode`),
        count: readWholeNumber(row["count"], `${at}.count`),
      })),
    },
    passedIn: {
      total: readWholeNumber(passedIn["total"], `${path}.passed_in.total`),
      bySource: countRowsOfWire(passedIn["by_source"], `${path}.passed_in.by_source`, (row, at) => ({
        voiceAgentId: readText(row["voice_agent_id"], `${at}.voice_agent_id`),
        displayName: readText(row["display_name"], `${at}.display_name`),
        count: readWholeNumber(row["count"], `${at}.count`),
      })),
    },
    passedBack: {
      made: readWholeNumber(passedBack["made"], `${path}.passed_back.made`),
      received: readWholeNumber(passedBack["received"], `${path}.passed_back.received`),
    },
    refused: {
      total: readWholeNumber(refused["total"], `${path}.refused.total`),
      byReason: countRowsOfWire(refused["by_reason"], `${path}.refused.by_reason`, (row, at) => ({
        reason: readText(row["reason"], `${at}.reason`),
        count: readWholeNumber(row["count"], `${at}.count`),
      })),
    },
    passedShare: share,
    timeServedSecond: readWholeNumber(one["time_served_second"], `${path}.time_served_second`),
  };
}

// ---------------------------------------------------------------------------------------
// The words (catalog `voiceAgentPassHistory`)
// ---------------------------------------------------------------------------------------

/** How a VoiceAgent is named on these screens: by the stints first, then by a list the screen holds. */
export type VoiceAgentNameOf = (voiceAgentId: string | null) => string | null;

export function voiceAgentNameOf(
  stint: readonly Pick<VoiceAgentStintView, "voiceAgentId" | "displayName">[],
  known: readonly { readonly voiceAgentId: string; readonly displayName: string }[],
): VoiceAgentNameOf {
  return (voiceAgentId) => {
    if (voiceAgentId === null) return null;
    return (
      stint.find((one) => one.voiceAgentId === voiceAgentId)?.displayName ??
      known.find((one) => one.voiceAgentId === voiceAgentId)?.displayName ??
      null
    );
  };
}

/** "With a summary" / "Straight away". */
export function voiceAgentPassModeTextOf(mode: VoiceAgentPassMode): string {
  return mode === "warm"
    ? portalText("voiceAgentPassHistory", "divider.with_summary")
    : portalText("voiceAgentPassHistory", "divider.straight_away");
}

/**
 * Why a pass did not happen, in the catalog's words: "it was outside its opening hours". `languageText` names the
 * conversation's language for `target_language_unavailable` ("it does not speak Greek"); without one the sentence names
 * the caller's language instead. Every other reason without a phrase of its own reads `reason.other`.
 */
export function voiceAgentPassReasonTextOf(reason: string | null, languageText: string | null): string {
  switch (reason) {
    case "target_not_live":
      return portalText("voiceAgentPassHistory", "reason.target_not_live");
    case "target_language_unavailable":
      return languageText === null
        ? portalText("voiceAgentPassHistory", "reason.target_language_unavailable_any")
        : portalText("voiceAgentPassHistory", "reason.target_language_unavailable", { language: languageText });
    case "target_medium_unsupported":
      return portalText("voiceAgentPassHistory", "reason.target_medium_unsupported");
    case "target_outside_opening_hours":
      return portalText("voiceAgentPassHistory", "reason.target_outside_opening_hours");
    case "target_at_line_limit":
      return portalText("voiceAgentPassHistory", "reason.target_at_line_limit");
    case "pass_limit_reached":
      return portalText("voiceAgentPassHistory", "reason.pass_limit_reached");
    case "person_handoff_in_progress":
      return portalText("voiceAgentPassHistory", "reason.person_handoff_in_progress");
    case "switch_off":
      return portalText("voiceAgentPassHistory", "reason.switch_off");
    case "pass_back_unavailable":
      return portalText("voiceAgentPassHistory", "reason.pass_back_unavailable");
    case "target_cannot_receive":
      return portalText("voiceAgentPassHistory", "reason.target_cannot_receive");
    // plan-022: an earlier end_conversation of the same line was ending the conversation.
    case "conversation_ending":
      return portalText("voiceAgentPassHistory", "reason.conversation_ending");
    default:
      return portalText("voiceAgentPassHistory", "reason.other");
  }
}

/**
 * "Could not pass to Delivery: it was outside its opening hours". A refusal whose target no screen can name (no target
 * was resolved, or it has since been deleted) reads "Could not pass: …" — the block's own words — never an id.
 */
export function voiceAgentPassRefusedTextOf(
  event: Pick<VoiceAgentPassEventView, "reason" | "targetVoiceAgentId">,
  nameOf: VoiceAgentNameOf,
  languageText: string | null,
): string {
  const reason = voiceAgentPassReasonTextOf(event.reason, languageText);
  const name = nameOf(event.targetVoiceAgentId);
  return name === null
    ? portalText("voiceAgentPassHistory", "refused.line_unnamed", { reason })
    : portalText("voiceAgentPassHistory", "refused.line", { name, reason });
}

/**
 * A pass in History's "What happened": "Passed to Delivery · With a summary", "Back with Customer support", "Could not
 * pass to Delivery: …". `null` for a `pass_started` (the event after it says how it went).
 */
export function voiceAgentPassSentenceOf(
  event: VoiceAgentPassEventView,
  nameOf: VoiceAgentNameOf,
  languageText: string | null,
): string | null {
  const target = nameOf(event.targetVoiceAgentId) ?? "—";
  switch (event.code) {
    case "pass_started":
      return null;
    case "pass_refused":
      return voiceAgentPassRefusedTextOf(event, nameOf, languageText);
    case "pass_back_completed":
      return portalText("voiceAgentPassHistory", "divider.back_with", { name: target });
    case "pass_completed": {
      const lead = portalText("voiceAgentPassHistory", "divider.passed_to", { name: target });
      return event.mode === null ? lead : `${lead} · ${voiceAgentPassModeTextOf(event.mode)}`;
    }
  }
}

/** "Outcome with Delivery: Order placed", or `null` when the stint reached none. */
export function voiceAgentStintOutcomeTextOf(stint: Pick<VoiceAgentStintView, "displayName" | "outcome">): string | null {
  return stint.outcome === null
    ? null
    : portalText("voiceAgentPassHistory", "outcome.with", { name: stint.displayName, outcome: stint.outcome });
}

/** "2 agents" on a History row, or `null` for a conversation one VoiceAgent answered. */
export function voiceAgentCountTextOf(voiceAgentCount: number | undefined): string | null {
  return voiceAgentCount === undefined || voiceAgentCount < 2
    ? null
    : portalText("voiceAgentPassHistory", "list.voice_agent_count", { count: voiceAgentCount });
}

/** "3 passed in from other agents" under History's figures, or `null` with none. */
export function passedInTextOf(passedInConversationCount: number | undefined): string | null {
  return passedInConversationCount === undefined || passedInConversationCount === 0
    ? null
    : portalText("voiceAgentPassHistory", "figure.passed_in", { count: passedInConversationCount });
}

/**
 * "Now with Delivery" on a board row whose conversation another VoiceAgent is answering, or `null` when the board's own
 * VoiceAgent is (or the source says nothing).
 */
export function nowWithTextOf(actingStint: ActingStintView | undefined, boardVoiceAgentId: string): string | null {
  return actingStint === undefined || actingStint.voiceAgentId === boardVoiceAgentId
    ? null
    : portalText("voiceAgentPassHistory", "live.now_with", { name: actingStint.displayName });
}

// ---------------------------------------------------------------------------------------
// The transcript with its dividers (History, the test console's stored read)
// ---------------------------------------------------------------------------------------

/** The line where a stint begins: "Passed to Delivery" · "With a summary", and what the next VoiceAgent was told. */
export interface VoiceAgentPassDividerView {
  readonly text: string;
  readonly modeText: string | null;
  /** "What Delivery was told" and the warm summary — absent for a reader who does not read transcripts. */
  readonly summary: { readonly label: string; readonly text: string } | null;
}

export type VoiceAgentPassTranscriptEntry<Item> =
  | {
      readonly kind: "item";
      readonly item: Item;
      /** The VoiceAgent that said an `assistant` line of a passed conversation; `null` keeps the usual label. */
      readonly voiceAgentName: string | null;
    }
  | { readonly kind: "divider"; readonly key: string; readonly divider: VoiceAgentPassDividerView }
  | { readonly kind: "refused"; readonly key: string; readonly text: string }
  | { readonly kind: "outcome"; readonly key: string; readonly text: string };

/**
 * The divider before stint `stint`: "Back with {name}" for a pass back; "Passed from {source}" where the screen's own
 * VoiceAgent is the one passed to (its History lists conversations passed in to it); "Passed to {name}" otherwise.
 */
export function voiceAgentPassDividerOf(
  stint: VoiceAgentStintView,
  stints: readonly VoiceAgentStintView[],
  event: readonly VoiceAgentPassEventView[],
  viewerVoiceAgentId: string,
): VoiceAgentPassDividerView {
  const completed = event.find(
    (one) => (one.code === "pass_completed" || one.code === "pass_back_completed") && one.toOrdinal === stint.ordinal,
  );
  const source = stints.find((one) => one.ordinal === stint.fromOrdinal);
  const text =
    stint.beganBy === "pass_back"
      ? portalText("voiceAgentPassHistory", "divider.back_with", { name: stint.displayName })
      : stint.voiceAgentId === viewerVoiceAgentId && source !== undefined && source.voiceAgentId !== viewerVoiceAgentId
        ? portalText("voiceAgentPassHistory", "divider.passed_from", { name: source.displayName })
        : portalText("voiceAgentPassHistory", "divider.passed_to", { name: stint.displayName });
  const mode: VoiceAgentPassMode | null =
    stint.beganBy === "warm_pass" ? "warm" : stint.beganBy === "cold_pass" ? "cold" : (completed?.mode ?? null);
  const said = completed?.summary ?? null;
  return {
    text,
    modeText: mode === null ? null : voiceAgentPassModeTextOf(mode),
    summary:
      said === null ? null : { label: portalText("voiceAgentPassHistory", "summary.label", { name: stint.displayName }), text: said },
  };
}

export interface VoiceAgentPassTranscriptInput<Item> {
  readonly item: readonly Item[];
  /** The transcript line an item is, or `null` for an entry that only keeps its place (a mixed conversation's switch). */
  readonly lineOf: (item: Item) => TranscriptLineView | null;
  readonly stint: readonly VoiceAgentStintView[];
  readonly event: readonly VoiceAgentPassEventView[];
  /** The VoiceAgent whose screen this is (History's, the tested one). */
  readonly viewerVoiceAgentId: string;
  readonly nameOf: VoiceAgentNameOf;
  /** The conversation's language in words, for "it does not speak {language}". */
  readonly languageText: string | null;
}

/**
 * The transcript with a divider where each stint begins, each assistant line named by its VoiceAgent, each stint's
 * outcome where it ends, and each refused pass where it happened (by time where both are known, else at the end of its
 * stint). `null` for a conversation one VoiceAgent answered with no pass event at all: the screen draws it as today.
 */
export function voiceAgentPassTranscriptOf<Item>(
  input: VoiceAgentPassTranscriptInput<Item>,
): readonly VoiceAgentPassTranscriptEntry<Item>[] | null {
  const stints = [...input.stint].sort((left, right) => left.ordinal - right.ordinal);
  const voiceAgentPassed = stints.length > 1;
  const refused = input.event.filter((one) => one.code === "pass_refused");
  if (!voiceAgentPassed && refused.length === 0) return null;

  const entry: VoiceAgentPassTranscriptEntry<Item>[] = [];
  const placed = new Set<VoiceAgentPassEventView>();
  const firstOrdinal = stints[0]?.ordinal ?? 1;
  let current = firstOrdinal;
  let closedThrough = firstOrdinal - 1;

  const refusedText = (one: VoiceAgentPassEventView): string =>
    voiceAgentPassRefusedTextOf(one, input.nameOf, input.languageText);
  const placeRefused = (ordinal: number, before: string | undefined): void => {
    const beforeMs = before === undefined ? Number.NaN : Date.parse(before);
    for (const one of refused) {
      if (placed.has(one) || one.voiceAgentStintOrdinal !== ordinal) continue;
      if (before !== undefined) {
        const atMs = one.occurredAt === null ? Number.NaN : Date.parse(one.occurredAt);
        if (Number.isNaN(beforeMs) || Number.isNaN(atMs) || atMs > beforeMs) continue;
      }
      placed.add(one);
      entry.push({ kind: "refused", key: `refused-${one.conversationEventId ?? String(entry.length)}`, text: refusedText(one) });
    }
  };
  const close = (ordinal: number): void => {
    if (ordinal <= closedThrough) return;
    closedThrough = ordinal;
    placeRefused(ordinal, undefined);
    const stint = stints.find((one) => one.ordinal === ordinal);
    const outcome = voiceAgentPassed && stint !== undefined ? voiceAgentStintOutcomeTextOf(stint) : null;
    if (outcome !== null) entry.push({ kind: "outcome", key: `outcome-${String(ordinal)}`, text: outcome });
  };
  const open = (through: number): void => {
    for (const stint of stints) {
      if (stint.ordinal <= current || stint.ordinal > through) continue;
      close(current);
      current = stint.ordinal;
      entry.push({
        kind: "divider",
        key: `divider-${String(stint.ordinal)}`,
        divider: voiceAgentPassDividerOf(stint, stints, input.event, input.viewerVoiceAgentId),
      });
    }
    current = Math.max(current, through);
  };

  for (const item of input.item) {
    const line = input.lineOf(item);
    if (line === null) {
      entry.push({ kind: "item", item, voiceAgentName: null });
      continue;
    }
    const ordinal = line.voiceAgentStintOrdinal ?? 1;
    if (ordinal > current) open(ordinal);
    placeRefused(current, line.startedAt);
    const name =
      voiceAgentPassed && line.speaker === "assistant"
        ? (stints.find((one) => one.ordinal === ordinal)?.displayName ?? null)
        : null;
    entry.push({ kind: "item", item, voiceAgentName: name });
  }
  const last = stints.at(-1)?.ordinal ?? current;
  open(last);
  close(current);
  for (const one of refused) {
    if (!placed.has(one)) entry.push({ kind: "refused", key: `refused-${one.conversationEventId ?? String(entry.length)}`, text: refusedText(one) });
  }
  return entry;
}

/** A pass event heard on the live transcript topic, and how many lines were on screen when it arrived. */
export interface VoiceAgentPassArrival {
  readonly event: VoiceAgentPassEventView;
  readonly afterLine: number;
}

/**
 * The test console's live transcript with its dividers, from the frames as they arrived (design §33.13.3): a completed
 * pass draws its divider after the lines heard before it and names every assistant line after it by the VoiceAgent it
 * reached; a refusal draws its line where it arrived. `null` until a pass event has arrived: the panel draws as today.
 */
export function voiceAgentPassLiveTranscriptOf<Item>(input: {
  readonly item: readonly Item[];
  readonly lineOf: (item: Item) => TranscriptLineView | null;
  readonly arrival: readonly VoiceAgentPassArrival[];
  readonly viewerVoiceAgentId: string;
  readonly nameOf: VoiceAgentNameOf;
  readonly languageText: string | null;
}): readonly VoiceAgentPassTranscriptEntry<Item>[] | null {
  const heard = input.arrival.filter((one) => one.event.code !== "pass_started");
  if (heard.length === 0) return null;
  const entry: VoiceAgentPassTranscriptEntry<Item>[] = [];
  let acting: string | null = null;
  let next = 0;
  const drain = (through: number): void => {
    while (next < heard.length && (heard[next]?.afterLine ?? Number.POSITIVE_INFINITY) <= through) {
      const one = heard[next];
      next += 1;
      if (one === undefined) continue;
      const key = `${one.event.code}-${one.event.conversationEventId ?? String(next)}`;
      if (one.event.code === "pass_refused") {
        entry.push({ kind: "refused", key, text: voiceAgentPassRefusedTextOf(one.event, input.nameOf, input.languageText) });
        continue;
      }
      const target = input.nameOf(one.event.targetVoiceAgentId) ?? "—";
      const source = input.nameOf(one.event.sourceVoiceAgentId);
      const text =
        one.event.code === "pass_back_completed"
          ? portalText("voiceAgentPassHistory", "divider.back_with", { name: target })
          : one.event.targetVoiceAgentId === input.viewerVoiceAgentId && source !== null
            ? portalText("voiceAgentPassHistory", "divider.passed_from", { name: source })
            : portalText("voiceAgentPassHistory", "divider.passed_to", { name: target });
      acting = target;
      entry.push({
        kind: "divider",
        key,
        divider: {
          text,
          modeText: one.event.mode === null ? null : voiceAgentPassModeTextOf(one.event.mode),
          summary:
            one.event.summary === null
              ? null
              : { label: portalText("voiceAgentPassHistory", "summary.label", { name: target }), text: one.event.summary },
        },
      });
    }
  };
  let lineIndex = 0;
  for (const item of input.item) {
    const line = input.lineOf(item);
    if (line !== null) {
      drain(lineIndex);
      lineIndex += 1;
    }
    entry.push({ kind: "item", item, voiceAgentName: line !== null && line.speaker === "assistant" ? acting : null });
  }
  drain(Number.POSITIVE_INFINITY);
  return entry;
}

// ---------------------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------------------

/** Capitalised for a table row, in the reader's language: "It was outside its opening hours". */
export function sentenceCaseOf(text: string): string {
  const first = text.at(0);
  return first === undefined ? text : `${first.toLocaleUpperCase()}${text.slice(1)}`;
}

/** Whether the block has anything to say: otherwise it reads `analytics.empty`. */
export function analyticsVoiceAgentPassIsEmpty(block: AnalyticsVoiceAgentPassView): boolean {
  return (
    block.passedOut.total === 0 &&
    block.passedIn.total === 0 &&
    block.passedBack.made === 0 &&
    block.passedBack.received === 0 &&
    block.refused.total === 0
  );
}

// ---------------------------------------------------------------------------------------
// The sandbox's twin
// ---------------------------------------------------------------------------------------

/**
 * The one `entry` stint a conversation that was never passed answers (API-READ synthesises it from the conversation
 * row): the sandbox console serves the same shape, so its screens read the field the api always sends.
 */
export function entryVoiceAgentStintOf(input: {
  readonly voiceAgentId: string;
  readonly displayName: string;
  readonly agentVersionId: string;
  readonly agentVersionLabel: string | null;
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly outcome: string | null;
}): VoiceAgentStintView {
  return { ...input, ordinal: 1, beganBy: "entry", fromOrdinal: null };
}

/** A sandbox VoiceAgent's analytics "Passes" block: it has passed nothing. */
export const NO_VOICE_AGENT_PASS_ANALYTICS: AnalyticsVoiceAgentPassView = {
  passedOut: { total: 0, byTarget: [] },
  passedIn: { total: 0, bySource: [] },
  passedBack: { made: 0, received: 0 },
  refused: { total: 0, byReason: [] },
  passedShare: 0,
  timeServedSecond: 0,
};

/**
 * The refusals by reason as the block lists them: each reason in words ("It was outside its opening hours"), and the
 * reasons that share `reason.other`'s words added into one row, so a row never repeats.
 */
export function refusedByReasonTextOf(
  byReason: AnalyticsVoiceAgentPassView["refused"]["byReason"],
): readonly { readonly text: string; readonly count: number }[] {
  const merged = new Map<string, number>();
  for (const one of byReason) {
    const text = sentenceCaseOf(voiceAgentPassReasonTextOf(one.reason, null));
    merged.set(text, (merged.get(text) ?? 0) + one.count);
  }
  return [...merged].map(([text, count]) => ({ text, count }));
}
